import { BaseButton } from '#src/base/button/base_button.tsx';
import { cn } from '#src/styling/cn.ts';
import { XIcon } from '@phosphor-icons/react';
import { Dialog as Primitive } from 'radix-ui';
import type { ComponentProps } from 'react';

export type DialogRootProps = ComponentProps<typeof Primitive.Root>;

const DialogRoot = (props: DialogRootProps) => {
  return <Primitive.Root data-slot="dialog" {...props} />;
};

export type DialogTriggerProps = ComponentProps<typeof Primitive.Trigger>;

const DialogTrigger = (props: DialogTriggerProps) => {
  return <Primitive.Trigger data-slot="dialog-trigger" {...props} />;
};

export type DialogPortalProps = ComponentProps<typeof Primitive.Portal>;

const DialogPortal = (props: DialogPortalProps) => {
  return <Primitive.Portal data-slot="dialog-portal" {...props} />;
};

export type DialogCloseProps = ComponentProps<typeof Primitive.Close>;

const DialogClose = (props: DialogCloseProps) => {
  return <Primitive.Close data-slot="dialog-close" {...props} />;
};

export type DialogOverlayProps = ComponentProps<typeof Primitive.Overlay>;

const DialogOverlay = ({ className, ...props }: DialogOverlayProps) => {
  return (
    <Primitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
        className,
      )}
      {...props}
    />
  );
};

export type DialogContentProps = ComponentProps<typeof Primitive.Content> & {
  showCloseButton?: boolean;
};

const DialogContent = ({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogContentProps) => {
  return (
    <DialogPortal>
      <DialogOverlay />
      <Primitive.Content
        data-slot="dialog-content"
        className={cn(
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg bg-white p-6 shadow-lg duration-200 sm:max-w-lg',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <BaseButton.Root
            asChild
            variant="ghost"
            size="icon-sm"
            className="absolute top-3 right-3"
          >
            <Primitive.Close data-slot="dialog-close">
              <BaseButton.Content variant="ghost" size="icon-sm">
                <XIcon className="size-4" />
                <span className="sr-only">Close</span>
              </BaseButton.Content>
            </Primitive.Close>
          </BaseButton.Root>
        )}
      </Primitive.Content>
    </DialogPortal>
  );
};

export type DialogHeaderProps = ComponentProps<'div'>;

const DialogHeader = ({ className, ...props }: DialogHeaderProps) => {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
};

export type DialogFooterProps = ComponentProps<'div'>;

const DialogFooter = ({ className, ...props }: DialogFooterProps) => {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
};

export type DialogTitleProps = ComponentProps<typeof Primitive.Title>;

const DialogTitle = ({ className, ...props }: DialogTitleProps) => {
  return (
    <Primitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-bold', className)}
      {...props}
    />
  );
};

export type DialogDescriptionProps = ComponentProps<typeof Primitive.Description>;

const DialogDescription = ({ className, ...props }: DialogDescriptionProps) => {
  return (
    <Primitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
};

export const Dialog = {
  Root: DialogRoot,
  Trigger: DialogTrigger,
  Portal: DialogPortal,
  Close: DialogClose,
  Overlay: DialogOverlay,
  Content: DialogContent,
  Header: DialogHeader,
  Footer: DialogFooter,
  Title: DialogTitle,
  Description: DialogDescription,
};
